import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import authService from '../services/authService';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
const SERVER_URL = API_BASE_URL.endsWith('/api') ? API_BASE_URL.slice(0, -4) : API_BASE_URL;

const SettingsPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('password');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [currentUser, setCurrentUser] = useState(null);

  // Password Change State
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  // Add Admin State
  const [adminData, setAdminData] = useState({
    username: '',
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
  });

  // Payment Settings State
  const [paymentSettings, setPaymentSettings] = useState({
    bank_name: '',
    account_number: '',
    ifsc_code: '',
    account_holder_name: '',
    upi_id: '',
    qr_code_url: '',
  });
  const [qrPreview, setQrPreview] = useState(null);
  const [qrBase64, setQrBase64] = useState('');
  const qrInputRef = useRef(null);

  useEffect(() => {
    const user = authService.getCurrentUser();
    setCurrentUser(user);
    loadPaymentSettings();
  }, []);

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      setMessage({ text: 'New passwords do not match!', type: 'error' });
      return;
    }

    if (passwordData.new_password.length < 6) {
      setMessage({ text: 'Password must be at least 6 characters!', type: 'error' });
      return;
    }

    try {
      setLoading(true);
      setMessage({ text: '', type: '' });
      
      await authService.changePassword({
        current_password: passwordData.current_password,
        new_password: passwordData.new_password,
      });

      setMessage({ text: 'Password changed successfully!', type: 'success' });
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      setMessage({ 
        text: err.response?.data?.message || 'Failed to change password', 
        type: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddAdmin = async (e) => {
    e.preventDefault();

    if (!adminData.username || !adminData.email || !adminData.password) {
      setMessage({ text: 'Please fill all required fields!', type: 'error' });
      return;
    }

    if (adminData.password.length < 6) {
      setMessage({ text: 'Password must be at least 6 characters!', type: 'error' });
      return;
    }

    try {
      setLoading(true);
      setMessage({ text: '', type: '' });
      
      await authService.createAdmin(adminData);

      setMessage({ text: 'Admin created successfully!', type: 'success' });
      setAdminData({
        username: '',
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        phone: '',
      });
    } catch (err) {
      setMessage({ 
        text: err.response?.data?.message || 'Failed to create admin', 
        type: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  const loadPaymentSettings = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch(`${API_BASE_URL}/settings/payment`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success && data.data) {
        setPaymentSettings({
          bank_name: data.data.bank_name || '',
          account_number: data.data.account_number || '',
          ifsc_code: data.data.ifsc_code || '',
          account_holder_name: data.data.account_holder_name || '',
          upi_id: data.data.upi_id || '',
          qr_code_url: data.data.qr_code_url || '',
        });
        if (data.data.qr_code_url) setQrPreview(data.data.qr_code_url);
      }
    } catch (_) {}
  };

  const handleQrFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setQrBase64(ev.target.result);
      setQrPreview(ev.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSavePaymentSettings = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setMessage({ text: '', type: '' });
      const token = localStorage.getItem('access_token');
      const payload = { ...paymentSettings };
      if (qrBase64) payload.qr_code_base64 = qrBase64;

      console.log('Sending payment settings:', payload);

      const res = await fetch(`${API_BASE_URL}/settings/payment`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      
      console.log('Response status:', res.status);
      const data = await res.json();
      console.log('Response data:', data);
      
      if (data.success) {
        setMessage({ text: 'Payment settings saved successfully!', type: 'success' });
        setQrBase64('');
        if (data.data.qr_code_url) {
          setPaymentSettings((prev) => ({ ...prev, qr_code_url: data.data.qr_code_url }));
          setQrPreview(data.data.qr_code_url);
        }
      } else {
        setMessage({ text: data.message || 'Failed to save settings', type: 'error' });
      }
    } catch (err) {
      console.error('Error saving payment settings:', err);
      setMessage({ text: `Failed to save payment settings: ${err.message}`, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout title="Settings">
      <style>{`
        .settings-container { max-width: 800px; margin: 0 auto; }
        .profile-banner { 
          background: linear-gradient(135deg, #84cc16 0%, #65a30d 100%);
          padding: 32px; border-radius: 16px; margin-bottom: 32px; color: white;
          box-shadow: 0 10px 20px -4px rgba(132, 204, 22, 0.4), 0 15px 25px -8px rgba(0, 0, 0, 0.15);
        }
        .profile-banner h2 { margin: 0 0 8px 0; font-size: 24px; }
        .profile-banner p { margin: 0; opacity: 0.9; font-size: 14px; }
        .profile-badge { 
          display: inline-block; padding: 6px 14px; background: rgba(255,255,255,0.25); 
          border-radius: 20px; font-size: 12px; margin-top: 12px; font-weight: 500;
        }
        .tabs { display: flex; gap: 8px; margin-bottom: 24px; }
        .tab { 
          padding: 12px 24px; border: 1px solid rgba(0, 0, 0, 0.04); background: white; 
          font-size: 14px; font-weight: 500; color: #6b7280; cursor: pointer; border-radius: 12px; 
          box-shadow: 0 4px 8px -2px rgba(132, 204, 22, 0.1), 0 6px 12px -4px rgba(0, 0, 0, 0.05); 
          transition: all 0.2s ease;
        }
        .tab:hover { 
          background: #f7fee7; transform: translateY(-1px); 
          box-shadow: 0 6px 12px -2px rgba(132, 204, 22, 0.15), 0 10px 16px -5px rgba(0, 0, 0, 0.06); 
        }
        .tab.active { 
          background: #f7fee7; color: #65a30d; 
          box-shadow: 0 6px 12px -2px rgba(132, 204, 22, 0.2), 0 10px 20px -5px rgba(0, 0, 0, 0.06); 
        }
        .card { 
          background: white; padding: 32px; border-radius: 16px; 
          border: 1px solid rgba(0, 0, 0, 0.04);
          box-shadow: 0 6px 12px -2px rgba(132, 204, 22, 0.15), 0 10px 20px -5px rgba(0, 0, 0, 0.05);
        }
        .card h3 { margin: 0 0 20px 0; font-size: 18px; color: #1f2937; }
        .form-group { margin-bottom: 20px; }
        .form-group label { 
          display: block; margin-bottom: 8px; font-weight: 500; 
          color: #374151; font-size: 14px; 
        }
        .form-group input { 
          width: 100%; padding: 12px 14px; border: 1px solid #d1d5db; 
          border-radius: 10px; font-size: 14px; box-sizing: border-box;
          transition: all 0.2s ease;
        }
        .form-group input:focus { 
          outline: none; border-color: #84cc16; 
          box-shadow: 0 0 0 3px rgba(132, 204, 22, 0.1); 
        }
        .form-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }
        .form-group.full-width { grid-column: span 2; }
        .btn-group { display: flex; gap: 12px; margin-top: 24px; }
        .btn { 
          padding: 12px 24px; border: none; border-radius: 10px; 
          cursor: pointer; font-weight: 500; font-size: 14px; transition: all 0.2s ease;
        }
        .btn-primary { 
          background: #84cc16; color: white; flex: 1;
          box-shadow: 0 4px 8px -2px rgba(132, 204, 22, 0.3);
        }
        .btn-primary:hover { 
          background: #65a30d; transform: translateY(-1px);
          box-shadow: 0 6px 12px -2px rgba(132, 204, 22, 0.4);
        }
        .btn-primary:disabled { background: #9ca3af; cursor: not-allowed; transform: none; }
        .btn-secondary { background: #f3f4f6; color: #374151; }
        .btn-secondary:hover { background: #e5e7eb; }
        .message { 
          padding: 12px 16px; border-radius: 10px; margin-bottom: 20px; 
          font-size: 14px; display: flex; align-items: center; gap: 8px;
        }
        .message.success { background: #d1fae5; color: #047857; border: 1px solid #a7f3d0; }
        .message.error { background: #fee2e2; color: #dc2626; border: 1px solid #fecaca; }
        .required { color: #dc2626; }
        .help-text { font-size: 12px; color: #6b7280; margin-top: 4px; }
        .info-box {
          background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 10px;
          padding: 16px; margin-bottom: 20px; font-size: 13px; color: #1e40af;
        }
        .qr-upload-area {
          border: 2px dashed #d1d5db; border-radius: 12px; padding: 24px;
          text-align: center; cursor: pointer; transition: all 0.2s;
          background: #f9fafb;
        }
        .qr-upload-area:hover { border-color: #84cc16; background: #f7fee7; }
        .qr-preview { width: 160px; height: 160px; object-fit: contain; 
          border-radius: 8px; border: 1px solid #e5e7eb; margin: 12px auto 0; display: block; }
        .section-divider { border: none; border-top: 1px solid #e5e7eb; margin: 24px 0; }
      `}</style>

      <div className="settings-container">
        <div className="profile-banner">
          <h2>👋 Hello, {currentUser?.first_name || currentUser?.username}!</h2>
          <p>✉️ {currentUser?.email || 'No email provided'}</p>
          <span className="profile-badge">🔐 System Administrator</span>
        </div>

        <div className="tabs">
          <button 
            className={`tab ${activeTab === 'password' ? 'active' : ''}`}
            onClick={() => setActiveTab('password')}
          >
            🔑 Change Password
          </button>
          <button 
            className={`tab ${activeTab === 'add-admin' ? 'active' : ''}`}
            onClick={() => setActiveTab('add-admin')}
          >
            ➕ Add Admin
          </button>
          <button
            className={`tab ${activeTab === 'payment' ? 'active' : ''}`}
            onClick={() => setActiveTab('payment')}
          >
            💳 Payment Settings
          </button>
        </div>

        {message.text && (
          <div className={`message ${message.type}`}>
            {message.type === 'success' ? '✅' : '⚠️'} {message.text}
          </div>
        )}

        {activeTab === 'password' && (
          <div className="card">
            <h3>Change Your Password</h3>
            <div className="info-box">
              🔒 For security reasons, please enter your current password before setting a new one.
            </div>
            <form onSubmit={handlePasswordChange}>
              <div className="form-group">
                <label>Current Password <span className="required">*</span></label>
                <input
                  type="password"
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                  placeholder="Enter your current password"
                  required
                />
              </div>
              <div className="form-group">
                <label>New Password <span className="required">*</span></label>
                <input
                  type="password"
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                  placeholder="Enter new password (min 6 characters)"
                  required
                  minLength={6}
                />
                <div className="help-text">Must be at least 6 characters long</div>
              </div>
              <div className="form-group">
                <label>Confirm New Password <span className="required">*</span></label>
                <input
                  type="password"
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                  placeholder="Confirm new password"
                  required
                />
              </div>
              <div className="btn-group">
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? '⏳ Updating...' : '🔒 Update Password'}
                </button>
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={() => navigate('/')}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {activeTab === 'add-admin' && (
          <div className="card">
            <h3>Add New Administrator</h3>
            <div className="info-box">
              ⚡ Create a new admin account with full system access privileges.
            </div>
            <form onSubmit={handleAddAdmin}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Username <span className="required">*</span></label>
                  <input
                    type="text"
                    value={adminData.username}
                    onChange={(e) => setAdminData({ ...adminData, username: e.target.value })}
                    placeholder="Enter username"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Email <span className="required">*</span></label>
                  <input
                    type="email"
                    value={adminData.email}
                    onChange={(e) => setAdminData({ ...adminData, email: e.target.value })}
                    placeholder="admin@example.com"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>First Name</label>
                  <input
                    type="text"
                    value={adminData.first_name}
                    onChange={(e) => setAdminData({ ...adminData, first_name: e.target.value })}
                    placeholder="First name"
                  />
                </div>
                <div className="form-group">
                  <label>Last Name</label>
                  <input
                    type="text"
                    value={adminData.last_name}
                    onChange={(e) => setAdminData({ ...adminData, last_name: e.target.value })}
                    placeholder="Last name"
                  />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={adminData.phone}
                    onChange={(e) => setAdminData({ ...adminData, phone: e.target.value })}
                    placeholder="+91 1234567890"
                  />
                </div>
                <div className="form-group">
                  <label>Password <span className="required">*</span></label>
                  <input
                    type="password"
                    value={adminData.password}
                    onChange={(e) => setAdminData({ ...adminData, password: e.target.value })}
                    placeholder="Minimum 6 characters"
                    required
                    minLength={6}
                  />
                </div>
              </div>
              <div className="btn-group">
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? '⏳ Creating...' : '➕ Create Admin'}
                </button>
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={() => setAdminData({ username: '', email: '', password: '', first_name: '', last_name: '', phone: '' })}
                >
                  Reset
                </button>
              </div>
            </form>
          </div>
        )}

        {activeTab === 'payment' && (
          <div className="card">
            <h3>💳 Payment Settings</h3>
            <div className="info-box">
              🏦 Configure bank details and QR code that students will see when making payments via Bank Transfer or QR Code.
            </div>
            <form onSubmit={handleSavePaymentSettings}>
              <p style={{ fontWeight: 600, color: '#374151', marginBottom: 12 }}>Bank Transfer Details</p>
              <div className="form-grid">
                <div className="form-group">
                  <label>Bank Name</label>
                  <input
                    type="text"
                    value={paymentSettings.bank_name}
                    onChange={(e) => setPaymentSettings({ ...paymentSettings, bank_name: e.target.value })}
                    placeholder="e.g. HDFC Bank"
                  />
                </div>
                <div className="form-group">
                  <label>Account Holder Name</label>
                  <input
                    type="text"
                    value={paymentSettings.account_holder_name}
                    onChange={(e) => setPaymentSettings({ ...paymentSettings, account_holder_name: e.target.value })}
                    placeholder="e.g. Sai Hostel Management"
                  />
                </div>
                <div className="form-group">
                  <label>Account Number</label>
                  <input
                    type="text"
                    value={paymentSettings.account_number}
                    onChange={(e) => setPaymentSettings({ ...paymentSettings, account_number: e.target.value })}
                    placeholder="Bank account number"
                  />
                </div>
                <div className="form-group">
                  <label>IFSC Code</label>
                  <input
                    type="text"
                    value={paymentSettings.ifsc_code}
                    onChange={(e) => setPaymentSettings({ ...paymentSettings, ifsc_code: e.target.value.toUpperCase() })}
                    placeholder="e.g. HDFC0001234"
                  />
                </div>
                <div className="form-group">
                  <label>UPI ID</label>
                  <input
                    type="text"
                    value={paymentSettings.upi_id}
                    onChange={(e) => setPaymentSettings({ ...paymentSettings, upi_id: e.target.value })}
                    placeholder="e.g. sai.hostel@upi"
                  />
                </div>
              </div>

              <hr className="section-divider" />
              <p style={{ fontWeight: 600, color: '#374151', marginBottom: 12 }}>QR Code Image</p>
              <div
                className="qr-upload-area"
                onClick={() => qrInputRef.current?.click()}
              >
                <input
                  ref={qrInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleQrFileChange}
                />
                {qrPreview ? (
                  <>
                    <img
                      src={qrPreview.startsWith('/uploads') ? `${SERVER_URL}${qrPreview}` : qrPreview}
                      alt="QR Code Preview"
                      className="qr-preview"
                    />
                    <p style={{ fontSize: 13, color: '#6b7280', marginTop: 8 }}>Click to replace QR code</p>
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: 40 }}>📷</div>
                    <p style={{ margin: '8px 0 0', color: '#6b7280', fontSize: 14 }}>Click to upload QR code image</p>
                    <p style={{ margin: '4px 0 0', color: '#9ca3af', fontSize: 12 }}>PNG, JPG up to 5MB</p>
                  </>
                )}
              </div>

              <div className="btn-group" style={{ marginTop: 24 }}>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? '⏳ Saving...' : '💾 Save Payment Settings'}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default SettingsPage;
